/**
 * HotTopicsHero — punchy "Hot right now" strip at the top of /community.
 * Three-column card row on desktop; horizontal scroll on mobile. Ranks
 * topics by reactions + replies with a 24h recency decay (server side).
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Flame, MessageSquare, ArrowUpRight, Pin, Lock, BarChart3, Calendar, Megaphone } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const rel = (iso) => {
  if (!iso) return '';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ''; }
};

const KIND_ICON = {
  poll: BarChart3, event: Calendar, announcement: Megaphone,
};

const HotTopicsHero = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/forum/hot?limit=3`);
        setTopics(r.data.topics || []);
      } catch { setTopics([]); }
      finally { setLoading(false); }
    })();
  }, [API]);

  if (loading || topics.length === 0) return null;

  const openTopic = (t) => {
    if (t.lesson_id) navigate(`/lesson/${t.lesson_id}`);
    else navigate(`/community/t/${t.id}`);
  };

  return (
    <div className="mb-5" data-testid="community-hot-topics">
      <div className="flex items-center gap-2 mb-2">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 text-amber-700 px-2.5 py-1">
          <Flame className="w-3.5 h-3.5" />
          <span className="text-[10px] uppercase tracking-widest font-extrabold">Hot right now</span>
        </div>
        <span className="text-[11px] text-muted-foreground">Trending in the last 24h</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {topics.map((t, i) => {
          const cat = t.category || {};
          const color = cat.color || '#F5B500';
          const KindIcon = KIND_ICON[t.kind];
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => openTopic(t)}
              data-testid={`community-hot-${t.id}`}
              className="text-left group relative overflow-hidden rounded-2xl border border-border bg-card p-4 hover:-translate-y-0.5 hover:shadow-xl hover:border-amber-500/50 transition-all"
            >
              {/* Rank number as a subtle bg watermark */}
              <div className="pointer-events-none absolute -top-3 -right-2 text-6xl font-black text-amber-500/10 select-none">
                {i + 1}
              </div>

              {/* Category chip + kind badge */}
              <div className="relative flex items-center gap-1.5 mb-2 flex-wrap">
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
                  style={{ backgroundColor: `${color}20`, color }}
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
                  {cat.name || 'Community'}
                </span>
                {KindIcon && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted text-foreground/70 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                    <KindIcon className="w-2.5 h-2.5" />
                    {t.kind}
                  </span>
                )}
                {t.is_pinned && <Pin className="w-3 h-3 text-amber-500" />}
                {t.is_locked && <Lock className="w-3 h-3 text-muted-foreground" />}
              </div>

              {/* Title */}
              <h3 className="relative font-heading text-base font-extrabold text-foreground leading-tight line-clamp-2 group-hover:text-amber-600 transition-colors">
                {t.title}
              </h3>

              {/* Meta */}
              <div className="relative mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
                <span className="truncate">{t.author?.name || 'Anonymous'} · {rel(t.last_activity_at)}</span>
                <span className="inline-flex items-center gap-1 font-bold text-foreground flex-shrink-0">
                  <MessageSquare className="w-3 h-3" />
                  {t.reply_count || 0}
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default HotTopicsHero;
